import { isEmpty, isEqual, isNil, isString } from 'lodash';
import moment from 'moment';
import { API } from '@shared/types/api';
import { CardMatch, getAllCardMatch, getAnyCardMatch, toCardMatch } from './cardMatch';
import { CardMatchLookups } from './cardMatchLookups';
import {
  IndexedValue,
  MatchComponent,
  getIndexedValues,
  getRelatedCardIds,
  getTime,
  normalizeKeyword,
} from './indexedValues';
import { getLetterTokens, getStandardTokens, matchesPhrasePrefix } from './phrasePrefix';

type FilterBranch = keyof API.FilterOneOf;

/**
 * The branches of a `FilterOneOf` in the order core-api declares them. It applies the first one
 * that's set and ignores the rest, so a tag selection of "Open" and "No value" sends both
 * `containsString` and `isEmpty` but filters on `containsString` alone.
 */
const FILTER_BRANCHES: FilterBranch[] = [
  'booleanIs',
  'containsCurrentUser',
  'containsNumber',
  'containsString',
  'dateIs',
  'dateRange',
  'dateTokenIs',
  'isEmpty',
  'matchesRelatedEntity',
  'numberIs',
  'referencedBy',
  'stringWildcard',
];

// Scalar representations core-api indexes as text, with its letters-only `scalar_text_analyzer`.
const SCALAR_TEXT_KEYS: API.ScalarKey[] = [
  'EMAIL_VALUE_STRING',
  'FILE_NAMES_STRING',
  'URL_VALUE_STRING',
];

/**
 * Whether a card passes a list query's `filterByComponents`, evaluated the way core-api's search
 * applies them. These are the filters the query sent - the collection's filters, its group's and,
 * in a tree view, the level's - so the answer is the one the server would give, short of the
 * differences noted where they arise.
 *
 * `listedCard` is the card as it was when the list last held it, set only for a card the list
 * holds. It passed every filter then, and a cross-workflow filter whose answer can't be worked out
 * here still passes while the card links to the same related cards.
 */
export function getCardFilterMatch(
  card: API.WorkflowEntity,
  filters: API.FilterByComponent[] | null | undefined,
  lookups: CardMatchLookups,
  listedCard?: API.WorkflowEntity,
): CardMatch {
  return getAllCardMatch(
    (filters ?? []).map(filter => {
      const match = getFilterMatch(card, filter, lookups);
      if (match === 'unknown' && hasSameRelatedCards(card, filter, listedCard)) {
        return 'match';
      }
      return match;
    }),
  );
}

function getFilterMatch(
  card: API.WorkflowEntity,
  filter: API.FilterByComponent,
  lookups: CardMatchLookups,
): CardMatch {
  const branch = getFilterBranch(filter.value);
  // core-api drops a filter with no branch set
  if (isNil(branch)) {
    return 'match';
  }
  if (branch === 'matchesRelatedEntity') {
    return getRelatedEntityMatch(card, filter, lookups);
  }
  const component = lookups.getComponent(card.workflowTemplateId, filter.componentId);
  if (isNil(component)) {
    return 'unknown';
  }
  const values = getIndexedValues(card, component, filter.scalarKey);
  if (isNil(values)) {
    return 'unknown';
  }
  return getBranchMatch(branch, filter, component, values, lookups);
}

function getBranchMatch(
  branch: FilterBranch,
  { scalarKey, value }: API.FilterByComponent,
  component: MatchComponent,
  values: IndexedValue[],
  lookups: CardMatchLookups,
): CardMatch {
  switch (branch) {
    case 'isEmpty':
      return toCardMatch(isEmpty(values) === value.isEmpty?.value);

    case 'containsString': {
      // tags compare through core-api's keyword normalizer; related card ids compare exactly
      const isKeyword =
        isNil(scalarKey) && (component.type === 'TAG' || component.type === 'ENTITY_BATCH');
      const normalize = (text: string) => (isKeyword ? normalizeKeyword(text) : text);
      const targets = (value.containsString?.values ?? []).map(normalize);
      const contains = values.some(item => isString(item) && targets.includes(normalize(item)));
      // a negated filter is `must_not terms`, which a card with no value passes
      return toCardMatch(value.containsString?.negate ? !contains : contains);
    }

    case 'containsNumber': {
      const targets = value.containsNumber?.values ?? [];
      const contains = values.some(item => targets.includes(item as number));
      return toCardMatch(value.containsNumber?.negate ? !contains : contains);
    }

    // core-api reads this as "contains the current user" whatever its `value`
    case 'containsCurrentUser':
      if (isNil(lookups.currentUserId)) {
        return 'unknown';
      }
      return toCardMatch(values.includes(lookups.currentUserId));

    case 'dateRange': {
      const after = value.dateRange?.after;
      const before = value.dateRange?.before;
      return toCardMatch(
        values.some(
          item =>
            (isNil(after) || getTime(item) >= getTime(after)) &&
            (isNil(before) || getTime(item) <= getTime(before)),
        ),
      );
    }

    // relative to the server's clock and not snapped to a day, as core-api resolves it
    case 'dateIs': {
      const { operator, absoluteDate, relativeDate } = value.dateIs!;
      const comparedTo = !isNil(absoluteDate)
        ? getTime(absoluteDate.timestamp)
        : moment(lookups.now)
            .add(relativeDate?.duration ?? 0, 'days')
            .valueOf();
      return getComparisonMatch(values.map(getTime), operator, comparedTo);
    }

    // core-api's "today" is the UTC day, not the customer's
    case 'dateTokenIs': {
      if (value.dateTokenIs?.value !== 'TODAY') {
        return 'unknown';
      }
      const today = moment.utc(lookups.now);
      const after = today.clone().startOf('day').valueOf();
      const before = today.clone().endOf('day').valueOf();
      return toCardMatch(values.some(item => getTime(item) >= after && getTime(item) <= before));
    }

    case 'numberIs': {
      const { operator, value: comparedTo } = value.numberIs!;
      if (operator === 'EQ') {
        return toCardMatch(values.includes(comparedTo));
      }
      return getComparisonMatch(values as number[], operator, comparedTo);
    }

    case 'booleanIs':
      return toCardMatch(values.includes(value.booleanIs!.value));

    case 'stringWildcard': {
      const getTokens = getTextTokenizer(component, scalarKey);
      if (isNil(getTokens)) {
        return 'unknown';
      }
      const queryTokens = getTokens(value.stringWildcard!.value);
      return toCardMatch(
        values.some(item => isString(item) && matchesPhrasePrefix(getTokens(item), queryTokens)),
      );
    }

    default:
      return 'unknown';
  }
}

/**
 * A cross-workflow filter passes when at least one linked card passes the filter nested in it.
 * core-api only matches through a linked card, so a card that links to none fails, even a negated
 * filter. The linked cards come from the event's refs, which reach one hop - a second hop's cards
 * are mostly absent, which leaves the answer unknown.
 */
function getRelatedEntityMatch(
  card: API.WorkflowEntity,
  filter: API.FilterByComponent,
  lookups: CardMatchLookups,
): CardMatch {
  const nestedFilter = filter.value.matchesRelatedEntity!.filterByComponent;
  // core-api checks the last hop's cards in a query of their own, which leaves out deleted cards,
  // and crosses any hop before it without filtering
  const isLastHop = getFilterBranch(nestedFilter.value) !== 'matchesRelatedEntity';
  return getAnyCardMatch(
    getRelatedCardIds(card.fields?.[filter.componentId]).map(relatedCardId => {
      const relatedCard = lookups.getRefCard(relatedCardId);
      if (isNil(relatedCard)) {
        return 'unknown';
      }
      if (isLastHop && !isNil(relatedCard.fields?.deletedAt)) {
        return 'noMatch';
      }
      return getFilterMatch(relatedCard, nestedFilter, lookups);
    }),
  );
}

function hasSameRelatedCards(
  card: API.WorkflowEntity,
  filter: API.FilterByComponent,
  listedCard: API.WorkflowEntity | undefined,
): boolean {
  if (isNil(listedCard) || getFilterBranch(filter.value) !== 'matchesRelatedEntity') {
    return false;
  }
  const getIds = (version: API.WorkflowEntity) =>
    getRelatedCardIds(version.fields?.[filter.componentId]).sort();
  return isEqual(getIds(listedCard), getIds(card));
}

function getFilterBranch(value: API.FilterOneOf): FilterBranch | undefined {
  return FILTER_BRANCHES.find(branch => !isNil(value[branch]));
}

function getComparisonMatch(
  values: number[],
  operator: API.ComparisonOperator,
  comparedTo: number,
): CardMatch {
  switch (operator) {
    case 'GT':
      return toCardMatch(values.some(item => item > comparedTo));
    case 'GTE':
      return toCardMatch(values.some(item => item >= comparedTo));
    case 'LT':
      return toCardMatch(values.some(item => item < comparedTo));
    case 'LTE':
      return toCardMatch(values.some(item => item <= comparedTo));
    default:
      return 'unknown';
  }
}

// A text component is analyzed as words; its scalar text representations as runs of letters.
function getTextTokenizer(
  component: MatchComponent,
  scalarKey: API.ScalarKey | null | undefined,
): ((text: string) => string[]) | undefined {
  if (isNil(scalarKey)) {
    return component.type === 'TEXT' ? getStandardTokens : undefined;
  }
  return SCALAR_TEXT_KEYS.includes(scalarKey) ? getLetterTokens : undefined;
}
